import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { auditAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Shield, Upload as UploadIcon, FileSpreadsheet, ArrowLeft,
  Check, Loader2, AlertTriangle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const analysisSteps = ['upload.step1', 'upload.step2', 'upload.step3', 'upload.step4'];

export default function Upload() {
  const { t, language } = useTranslation();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(-1);
  const [error, setError] = useState('');

  const onDrop = useCallback((accepted) => {
    if (accepted.length > 0) {
      setFile(accepted[0]);
      setError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
    onDropRejected: () => setError(t('upload.drop_reject')),
  });

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setCurrentStep(0);

    try {
      const onProgress = (e) => {
        const pct = Math.round((e.loaded * 100) / e.total);
        setUploadProgress(pct);
        if (pct >= 100) setCurrentStep(1);
      };

      // Simulate step progression after upload
      const stepTimer = setTimeout(() => setCurrentStep(2), 3000);
      const stepTimer2 = setTimeout(() => setCurrentStep(3), 6000);

      const res = await auditAPI.upload(file, language, onProgress);

      clearTimeout(stepTimer);
      clearTimeout(stepTimer2);

      if (res.data?.audit_id) {
        await refreshUser();
        navigate(`/report/${res.data.audit_id}`, { state: { auditData: res.data } });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const isRateLimit = err.response?.status === 403 && (typeof detail === 'string') &&
        (detail.includes('2 audits') || detail.includes('Limite') || detail.includes('audits max'));
      setError(isRateLimit ? 'RATE_LIMIT' : (detail || t('upload.failed')));
      setUploading(false);
      setCurrentStep(-1);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="upload-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#050505]/90 backdrop-blur-lg">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center gap-3">
          <Link to="/dashboard" className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors" data-testid="upload-back-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
          <span className="font-heading text-lg tracking-tight">{t('upload.title')}</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {!uploading ? (
          <>
            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-sm p-16 text-center cursor-pointer transition-all ${
                isDragActive ? 'border-blue-600 bg-blue-600/5' :
                file ? 'border-green-600/50 bg-green-600/5' :
                'border-white/10 hover:border-white/20 bg-[#0A0A0A]'
              }`}
              data-testid="dropzone"
            >
              <input {...getInputProps()} data-testid="file-input" />
              {file ? (
                <div className="animate-fade-in">
                  <FileSpreadsheet className="w-12 h-12 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
                  <p className="font-body text-white text-base mb-1">{file.name}</p>
                  <p className="font-mono text-neutral-500 text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <>
                  <UploadIcon className="w-12 h-12 text-neutral-600 mx-auto mb-4" strokeWidth={1.5} />
                  <p className="font-body text-white text-base mb-1">{t('upload.drag')}</p>
                  <p className="font-body text-neutral-500 text-sm mb-4">{t('upload.or')}</p>
                  <p className="font-mono text-neutral-600 text-xs">{t('upload.hint')}</p>
                </>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-600/10 border border-red-600/20 rounded-sm flex items-center gap-2" data-testid="upload-error">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-red-400 text-sm font-body">
                  {error === 'RATE_LIMIT' ? (
                    <>
                      {t('rate.limit_prefix')}{' '}
                      <a href="/#pricing" className="text-blue-400 hover:text-blue-300 underline font-medium">
                        {t('rate.limit_link')}
                      </a>
                      {' '}{t('rate.limit_suffix')}
                    </>
                  ) : (
                    error
                  )}
                </span>
              </div>
            )}

            {file && (
              <div className="mt-6 flex justify-center">
                <Button onClick={handleUpload}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-10 text-xs font-body tracking-widest rounded-sm"
                  data-testid="start-audit-button">
                  <UploadIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t('upload.cta')}
                </Button>
              </div>
            )}

            {/* Security Badge */}
            <div className="mt-8 flex items-center justify-center gap-2 p-3 border border-white/5 bg-white/[0.01] rounded-sm" data-testid="security-badge">
              <svg className="w-4 h-4 text-green-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span className="text-neutral-500 text-xs font-body">{t('security.badge')}</span>
            </div>
          </>
        ) : (
          /* Analysis Progress */
          <div className="border border-white/10 bg-[#0A0A0A] rounded-sm p-10" data-testid="analysis-progress">
            <div className="flex items-center gap-3 mb-8">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" strokeWidth={1.5} />
              <h2 className="font-heading text-xl text-white">{t('upload.analyzing')}</h2>
            </div>

            {/* Upload progress bar */}
            <div className="mb-8">
              <div className="flex justify-between text-xs font-body text-neutral-500 mb-2">
                <span>{file?.name}</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-1.5 bg-white/5 [&>div]:bg-blue-600" />
            </div>

            {/* Steps */}
            <div className="space-y-4">
              {analysisSteps.map((stepKey, i) => (
                <div key={stepKey} className="flex items-center gap-3" data-testid={`analysis-step-${i}`}>
                  {currentStep > i ? (
                    <div className="w-6 h-6 bg-green-600/20 flex items-center justify-center rounded-full">
                      <Check className="w-3.5 h-3.5 text-green-500" strokeWidth={2} />
                    </div>
                  ) : currentStep === i ? (
                    <div className="w-6 h-6 bg-blue-600/20 flex items-center justify-center rounded-full">
                      <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" strokeWidth={2} />
                    </div>
                  ) : (
                    <div className="w-6 h-6 bg-white/5 flex items-center justify-center rounded-full">
                      <div className="w-1.5 h-1.5 bg-neutral-600 rounded-full" />
                    </div>
                  )}
                  <span className={`font-body text-sm ${currentStep >= i ? 'text-white' : 'text-neutral-600'}`}>
                    {t(stepKey)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
